import { type QueryKey } from '@tanstack/react-query'

import { type TAudioPaginated, type TAudioSimple } from '@core/models'

import { queryClient } from '@core/query'
import { AudioQueryKey } from './queryKeys'

export const getStoredAudios = (username: string) => {
    return queryClient.getQueryData<TAudioSimple[]>(AudioQueryKey.authorAllAudios(username))
}

export const updateAudios = (username: string, newAudios: TAudioSimple[]) => {
    queryClient.setQueryData<TAudioSimple[]>(AudioQueryKey.authorAllAudios(username), newAudios)
}

export const removeAudioFromAudios = ({
    username,
    audioSlug,
}: {
    username: string
    audioSlug: string
}) => {
    const audios = getStoredAudios(username)

    if (!audios) return undefined

    const newAudios = audios.filter(audio => audio.slug !== audioSlug)

    updateAudios(username, newAudios)

    return newAudios
}

export type TAuthorPagesSnapshot = [QueryKey, TAudioPaginated | undefined][]

export const removeAudioFromAuthorPages = ({
    username,
    audioSlug,
}: {
    username: string
    audioSlug: string
}): TAuthorPagesSnapshot => {
    const snapshot = queryClient.getQueriesData<TAudioPaginated>({
        queryKey: AudioQueryKey.authorAudioPages(username),
    })

    queryClient.setQueriesData<TAudioPaginated>(
        { queryKey: AudioQueryKey.authorAudioPages(username) },
        page =>
            page && page.items.some(audio => audio.slug === audioSlug)
                ? {
                      items: page.items.filter(audio => audio.slug !== audioSlug),
                      pagination: {
                          ...page.pagination,
                          total: Math.max(0, page.pagination.total - 1),
                      },
                  }
                : page,
    )

    return snapshot
}

export const restoreAuthorPages = (snapshot: TAuthorPagesSnapshot) => {
    snapshot.forEach(([queryKey, page]) => {
        queryClient.setQueryData<TAudioPaginated>(queryKey, page)
    })
}

export const resetAllAuthorAudios = ({ username }: { username: string }) => {
    return Promise.all([
        queryClient.invalidateQueries({
            queryKey: ['audios'],
        }),
        queryClient.invalidateQueries({
            queryKey: ['audio'],
        }),
        queryClient.invalidateQueries({
            queryKey: ['user', 'paginated-audios'],
        }),
        queryClient.invalidateQueries({
            queryKey: AudioQueryKey.authorAudioPages(username),
        }),
        queryClient.resetQueries({
            queryKey: AudioQueryKey.authorAllAudios(username),
        }),
    ])
}

// By prefix, so this module never names another feature's key. The id-first shape it relies on is
// pinned by contract tests in `audio-download` and `comment`.
export const removeAudioQueries = (audioId: string) => {
    queryClient.removeQueries({ queryKey: [audioId] })
}

// Predicate rather than an import: the collection barrel would drag the whole feature, analytics and
// ky in here. The shape is pinned by a contract test in `features/collection`.
const AUTHOR_COLLECTION_SEGMENTS = ['collections', 'paginated-collections']

const isAuthorCollectionKey = (queryKey: QueryKey) =>
    queryKey[0] === 'author' && AUTHOR_COLLECTION_SEGMENTS.includes(String(queryKey[2]))

const isCollectionTracklistKey = (queryKey: QueryKey) =>
    queryKey[0] === 'collection' && queryKey[1] === 'audios'

export const revalidateAuthorCollections = () => {
    return queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
            isAuthorCollectionKey(queryKey) || isCollectionTracklistKey(queryKey),
    })
}
